import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CustomerAccount, Customer } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { MAXIMUM_3A_CONTRIBUTION } from '@app/app.constants';
import { FinancialSituationBankDetail } from '@shared/models/Account';
import { ToastNotificationService } from '@shared/services/toast-notification.service';

@Component({
  selector: 'app-fund-account-details',
  templateUrl: './fund-account-details.component.html',
  styleUrls: ['./fund-account-details.component.scss'],
  standalone: false
})
export class FundAccountDetailsComponent implements OnInit {
  @Input() customer: Customer;
  @Input() currentAccount: CustomerAccount;
  @Input() financialDetails: null | { iban: number; bank: FinancialSituationBankDetail };
  @Output() contactSupport: EventEmitter<void> = new EventEmitter<void>();
  @Output() getFinancialSituation: EventEmitter<void> = new EventEmitter<void>();
  @Output() trackActivity: EventEmitter<string> = new EventEmitter<string>();
  isPossibleToCreate3A = false;
  stillPossibleToInvest: number;

  get accountKind() {
    switch (this.currentAccount.kind) {
      case '3a':
        return this.translateService.instant('productSwitchDropdown.3aAccount') as string;
      case '3b':
        return this.translateService.instant('productSwitchDropdown.3bAccount') as string;
      case 'kid':
        return this.currentAccount.owner_name;
      default:
        return '';
    }
  }

  get customerName() {
    return `${this.customer.first_name} ${this.customer.last_name}`;
  }

  constructor(
    private translateService: TranslateService,
    private toastNotificationService: ToastNotificationService
  ) {}

  ngOnInit(): void {
    if (this.customer && this.customer.app_location !== 'ch') {
      this.getFinancialSituation.emit();
    }

    if (this.currentAccount && this.currentAccount.kind === '3a') {
      this.isPossibleToCreate3A = this.currentAccount.max_contribution_level !== 'transfer_only';
      const possibleToInvest =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        MAXIMUM_3A_CONTRIBUTION[new Date().getFullYear()][this.currentAccount.max_contribution_level] - this.currentAccount.deposit_this_year;
      this.stillPossibleToInvest = possibleToInvest < 0 ? 0 : possibleToInvest;
    }
  }

  onCopyToClipboard(value: string) {
    let copyValue = '';
    switch (value) {
      case 'address':
        copyValue = this.translateService.instant('reinvest.bankAddress');
        break;
      case 'swift':
        copyValue = this.translateService.instant('steps.id5.saxo_funded.swiftBic');
        break;
      case 'hypiAddress':
        copyValue = this.translateService.instant('sp.revamp.accountDetails.3a.hypiAddress').replace(/<[^>]+>/g, '');
        break;
      case 'libertyAddress':
        copyValue = this.translateService.instant('sp.revamp.accountDetails.3a.libertyAddress').replace(/<[^>]+>/g, '');
        break;
      default:
        copyValue = value;
        break;
    }

    const textarea = document.createElement('textarea');
    textarea.textContent = copyValue;
    textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
      const success = document.execCommand('copy'); // Security exception may be thrown by some browsers.
      if (success) {
        this.toastNotificationService.show('success', this.translateService.instant('sp.revamp.accountDetails.copied') as string);
      }
    } catch (ex) {
      window.prompt('Copy to clipboard: Ctrl+C, Enter', copyValue);
    } finally {
      document.body.removeChild(textarea);
    }
  }

  onContactSupport() {
    this.contactSupport.emit();
  }
}
